import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button, Input } from "@shop-ledger/ui-components";
import { usePlatformClient } from "../../api/ClientContext";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";
import {
  SHIPMENT_STATUSES,
  type MarketplaceOrderRollupStatus,
  type PlatformMarketplaceSuborderDetail,
  type ShipmentStatus,
} from "../../api/types";

function statusBadgeClass(status: MarketplaceOrderRollupStatus): string {
  switch (status) {
    case "DELIVERED":
      return "badge badge--active";
    case "FAILED":
      return "badge badge--suspended";
    case "PENDING":
      return "badge badge--fresh";
    default:
      return "badge badge--demo";
  }
}

/**
 * One suborder's line items plus an inline status/carrier/tracking-number editor —
 * `PATCH /platform/marketplace/orders/{orderId}/suborders/{suborderId}`. An illegal
 * `status` transition (`shipping_service.validate_transition`) 409s; that error message
 * ("Cannot transition shipment from X to Y") is surfaced verbatim via a `StatusBanner`
 * scoped to this card, not swallowed and not a page-wide failure — the operator can retry
 * with a legal value without losing the rest of the order's already-loaded state.
 */
function SuborderEditor({
  orderId,
  suborder,
  onSaved,
}: {
  orderId: string;
  suborder: PlatformMarketplaceSuborderDetail;
  onSaved: () => void;
}) {
  const client = usePlatformClient();
  const [status, setStatus] = useState<ShipmentStatus>(suborder.shipment_status);
  const [carrier, setCarrier] = useState(suborder.carrier);
  const [trackingNumber, setTrackingNumber] = useState(suborder.tracking_number ?? "");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    setPending(true);
    setError(null);
    try {
      const updated = await client.updateMarketplaceSuborderStatus(orderId, suborder.suborder_id, {
        status,
        carrier,
        tracking_number: trackingNumber || undefined,
      });
      // Reflect the server's own saved values immediately (not just an optimistic echo of
      // what was submitted) — `onSaved` below also triggers a full order reload so the
      // page-level rollup status stays live-accurate too.
      setStatus(updated.shipment_status);
      setCarrier(updated.carrier);
      setTrackingNumber(updated.tracking_number ?? "");
      onSaved();
    } catch (err) {
      // A 409 (illegal transition) and any other failure both land here, surfaced with the
      // backend's own detail message (`ApiError`/mock client both throw with that text) —
      // never silently swallowed.
      setError(err instanceof Error ? err.message : "Failed to update this suborder.");
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="panel">
      <div className="page__header" style={{ marginBottom: 10 }}>
        <div>
          <strong>{suborder.tenant_name}</strong>
          <p className="muted" style={{ margin: "2px 0 0" }}>
            Sale {suborder.sale_id.slice(0, 8)}… · Sub-total {suborder.sub_total} · Total {suborder.grand_total}
          </p>
        </div>
        <span className={`badge ${suborder.shipment_status === "DELIVERED" ? "badge--active" : suborder.shipment_status === "FAILED" ? "badge--suspended" : "badge--demo"}`}>
          {suborder.shipment_status}
        </span>
      </div>

      {error ? <StatusBanner kind="error">{error}</StatusBanner> : null}

      <table className="sl-data-table" style={{ marginBottom: 16 }}>
        <thead>
          <tr>
            <th className="sl-data-table__th sl-data-table__th--left">Item</th>
            <th className="sl-data-table__th sl-data-table__th--right">Qty</th>
            <th className="sl-data-table__th sl-data-table__th--right">Unit price</th>
            <th className="sl-data-table__th sl-data-table__th--right">Total</th>
          </tr>
        </thead>
        <tbody>
          {suborder.items.map((item) => (
            <tr key={item.inventory_item_id} className="sl-data-table__row">
              <td className="sl-data-table__td sl-data-table__td--left">{item.name}</td>
              <td className="sl-data-table__td sl-data-table__td--right">{item.quantity}</td>
              <td className="sl-data-table__td sl-data-table__td--right">{item.unit_price}</td>
              <td className="sl-data-table__td sl-data-table__td--right">{item.total_price}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="form-grid">
        <div className="sl-field">
          <label className="sl-field__label" htmlFor={`status-${suborder.suborder_id}`}>
            Shipment status
          </label>
          <select
            id={`status-${suborder.suborder_id}`}
            className="sl-input"
            value={status}
            onChange={(e) => setStatus(e.target.value as ShipmentStatus)}
          >
            {SHIPMENT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <Input label="Carrier" value={carrier} onChange={(e) => setCarrier(e.target.value)} />
        <Input
          label="Tracking number"
          value={trackingNumber}
          onChange={(e) => setTrackingNumber(e.target.value)}
          placeholder="e.g. SPX-8842-UG"
        />
      </div>

      <div className="form-actions">
        <Button type="button" variant="primary" size="md" disabled={pending} onClick={() => void handleSave()}>
          {pending ? "Saving…" : "Save"}
        </Button>
        {suborder.shipped_at ? (
          <span className="muted">Shipped {new Date(suborder.shipped_at).toLocaleString()}</span>
        ) : null}
        {suborder.delivered_at ? (
          <span className="muted">Delivered {new Date(suborder.delivered_at).toLocaleString()}</span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * §16's full marketplace order detail: contact/address, then one card per seller
 * ("suborder"), each with its own live line items and inline fulfillment editor. A
 * suborder save triggers a full order `reload()` (not just a local field patch) so the
 * page-level rollup status badge and every other suborder's context stay live-accurate —
 * cheap at this scale, and simpler than hand-merging one row's update into cached state.
 */
export function MarketplaceOrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const client = usePlatformClient();
  const {
    data: order,
    loading,
    error,
    reload,
  } = useAsyncData(
    () => (orderId ? client.getMarketplaceOrder(orderId) : Promise.reject(new Error("Missing order id"))),
    [client, orderId],
  );

  if (error) {
    return (
      <div className="page">
        <StatusBanner kind="error">{error}</StatusBanner>
      </div>
    );
  }

  if (loading || !order) {
    return (
      <div className="page">
        <p className="muted">Loading order…</p>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Order {order.id.slice(0, 8)}…</h1>
          <p className="page__subtitle">
            Placed {new Date(order.created_at).toLocaleString()} ·{" "}
            <span className={statusBadgeClass(order.status)}>{order.status}</span>
          </p>
        </div>
        <Link to="/marketplace/orders" className="sl-button sl-button--ghost sl-button--md">
          Back to orders
        </Link>
      </div>

      <div className="panel">
        <h2 className="panel__title">Contact &amp; shipping address</h2>
        <div className="form-grid">
          <div>
            <p className="muted" style={{ margin: 0 }}>
              Name
            </p>
            <p style={{ margin: 0 }}>{order.contact_name}</p>
          </div>
          <div>
            <p className="muted" style={{ margin: 0 }}>
              Email
            </p>
            <p style={{ margin: 0 }}>{order.contact_email}</p>
          </div>
          <div>
            <p className="muted" style={{ margin: 0 }}>
              Phone
            </p>
            <p style={{ margin: 0 }}>{order.contact_phone}</p>
          </div>
          <div>
            <p className="muted" style={{ margin: 0 }}>
              Order total
            </p>
            <p style={{ margin: 0 }}>
              {order.grand_total} {order.currency ?? ""}
            </p>
          </div>
        </div>
        <p className="muted" style={{ marginTop: 10 }}>
          {order.address_line1}
          {order.address_line2 ? `, ${order.address_line2}` : ""}, {order.city}
          {order.region ? `, ${order.region}` : ""}
          {order.postal_code ? ` ${order.postal_code}` : ""}, {order.country}
        </p>
      </div>

      <h2 className="panel__title">
        Suborders ({order.suborders.length} seller{order.suborders.length === 1 ? "" : "s"})
      </h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {order.suborders.map((suborder) => (
          <SuborderEditor key={suborder.suborder_id} orderId={order.id} suborder={suborder} onSaved={reload} />
        ))}
      </div>
    </div>
  );
}
